import { format, isSameDay } from 'date-fns';
import { useFormikContext } from 'formik';
import { useState } from 'react';

import { Callout } from '../../../cdl/Callout/Callout';
import { DatePicker } from '../../../cdl/DatePicker/DatePicker';
import { Label } from '../../../cdl/Label/Label';
import { Radio, RadioGroup, RadioGroupItem } from '../../../cdl/RadioGroup/RadioGroup';
import { ReadOnlyField } from '../../../cdl/ReadOnlyField/ReadOnlyField';
import { Text } from '../../../cdl/Text/Text';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { IconArrowRight } from '../../../common/icons/cdl/ArrowRight';
import { IconLock } from '../../../common/icons/cdl/Lock';
import { Box } from '../../../common/ui/Box';
import { useDemandDefinitionReferencePeriodPresets } from '../../hooks/useDemandDefinitionReferencePeriodPresets';
import type { DemandDefinitionState } from '../../types/DemandDefinitionState';

const CUSTOM_REFERENCE_PERIOD = 'custom';

interface ReferencePeriodStepProps {
    isLocked: boolean;
}

export const ReferencePeriodStep = ({ isLocked }: ReferencePeriodStepProps) => {
    const { errors, setTouched, setValues, touched, values } = useFormikContext<DemandDefinitionState>();
    const presets = useDemandDefinitionReferencePeriodPresets();
    const { referencePeriodFrom, referencePeriodTo } = values;
    const matchingPreset =
        referencePeriodFrom && referencePeriodTo
            ? presets.find(
                  (preset) => isSameDay(preset.from, referencePeriodFrom) && isSameDay(preset.to, referencePeriodTo)
              )
            : undefined;
    const [selectedOption, setSelectedOption] = useState(matchingPreset?.label ?? CUSTOM_REFERENCE_PERIOD);
    const updateReferencePeriod = (nextValues: DemandDefinitionState) => {
        setTouched({ ...touched, fleetChanges: [] }, false);
        setValues({ ...nextValues, fleetChanges: [] });
    };

    if (isLocked && referencePeriodFrom && referencePeriodTo) {
        return (
            <Box display="flex" flexDirection="column" gap={24}>
                <Callout Icon={IconLock} description={translate('demandDefinition.referencePeriodLockedDescription')} />
                <ReadOnlyField
                    label={translate('demandDefinition.referencePeriod')}
                    value={`${format(referencePeriodFrom, 'MMM d, yyyy')}${Characters.NO_BREAK_SPACE}${Characters.EN_DASH}${Characters.NO_BREAK_SPACE}${format(referencePeriodTo, 'MMM d, yyyy')}`}
                />
            </Box>
        );
    }

    return (
        <Box display="flex" flexDirection="column" gap={8}>
            <Text variant="small" color="foreground.muted">
                {translate('demandDefinition.dateRange')}
            </Text>
            <RadioGroup
                value={selectedOption}
                onValueChange={(option) => {
                    const customReferencePeriod =
                        selectedOption === CUSTOM_REFERENCE_PERIOD
                            ? { from: referencePeriodFrom, to: referencePeriodTo }
                            : values.customReferencePeriod;
                    setSelectedOption(option);
                    const preset = presets.find((candidate) => candidate.label === option);
                    updateReferencePeriod({
                        ...values,
                        customReferencePeriod,
                        referencePeriodFrom: preset ? preset.from : customReferencePeriod?.from,
                        referencePeriodTo: preset ? preset.to : customReferencePeriod?.to,
                    });
                }}
            >
                {presets.map((preset) => (
                    <RadioGroupItem key={preset.label}>
                        <Radio value={preset.label} />
                        <Label>
                            <Box as="span" display="flex" alignItems="center" justifyContent="space-between" gap={16}>
                                <Text>{preset.label}</Text>
                                <Text
                                    variant="small"
                                    color={selectedOption === preset.label ? 'foreground.default' : 'foreground.subtle'}
                                >
                                    {format(preset.from, 'MMM yyyy')}
                                    {Characters.NO_BREAK_SPACE}
                                    {Characters.EN_DASH}
                                    {Characters.NO_BREAK_SPACE}
                                    {format(preset.to, 'MMM yyyy')}
                                </Text>
                            </Box>
                        </Label>
                    </RadioGroupItem>
                ))}
                <RadioGroupItem>
                    <Radio value={CUSTOM_REFERENCE_PERIOD} />
                    <Label>
                        <Text>{translate('global.datePicker.presets.custom')}</Text>
                    </Label>
                </RadioGroupItem>
            </RadioGroup>
            {selectedOption === CUSTOM_REFERENCE_PERIOD ? (
                <Box
                    display="grid"
                    gridTemplateColumns="1fr auto 1fr"
                    alignItems="start"
                    gap={8}
                    marginLeft={28}
                    marginTop={8}
                >
                    <DatePicker
                        label={translate('demandDefinition.from')}
                        date={values.referencePeriodFrom}
                        isClearable={false}
                        onDateChange={(date) => updateReferencePeriod({ ...values, referencePeriodFrom: date })}
                    />
                    <Box color="foreground.muted" marginTop={28} height={32} display="flex" alignItems="center">
                        <IconArrowRight />
                    </Box>
                    <DatePicker
                        label={translate('demandDefinition.to')}
                        date={values.referencePeriodTo}
                        isClearable={false}
                        negative={Boolean(values.referencePeriodTo && errors.referencePeriodTo)}
                        caption={values.referencePeriodTo ? errors.referencePeriodTo : undefined}
                        onDateChange={(date) => updateReferencePeriod({ ...values, referencePeriodTo: date })}
                    />
                </Box>
            ) : null}
        </Box>
    );
};
